/***********************/
/* Angular + external */
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject } from "rxjs";
/***********************/
import { BfxDialogData } from "./models/bfx-dialog-data.model";
// components
import { BfxDialogBaseComponent } from "./bfx-dialog-base.component";
// services

import { LoggerService } from "@bfx/logger";
import {
  ActiveUser,
  ActiveUserProfilePromiseService,
  SystemConfigPromiseService,
} from "shared/auth-services";
import { ErrorsImpl } from "shared/cubiq-model-multi-asset";
import { UserPromiseLookupService } from "shared/cubiq-services";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
const logger = LoggerService.for(__dirname);
export enum PasswordField {
  OLD_PASSWORD,
  NEW_PASSWORD,
  NEW_PASSWORD_CONFIRM,
}

const ERROR_RUNTIME: string = "runtime.exception";
const ERROR_INVALID_OLD_PASS: string = "pwd.current.incorrect";

const OLD_PASS_ERRORS: Array<string> = [ERROR_INVALID_OLD_PASS];
const SYSTEM_ERRORS: Array<string> = [ERROR_RUNTIME];

const MINIMUM_PASSWORD_LENGTH: number = 4;
const MAXIMUM_PASSWORD_LENGTH: number = 30;
const MINIMUM_PASSWORD_LENGTH_SYSCONF: string = "pwd.req.minLength";
const MAXIMUM_PASSWORD_LENGTH_SYSCONF: string = "pwd.req.maxLength";

const STRENGTH_MESSAGES: Array<string> = ["Low", "Medium", "High", "Very high"];
const STRENGTH_COLORS: Array<string> = [
  "low-strength",
  "medium-strength",
  "high-strength",
  "very-high-strength",
];

/**
 * Change password dialog with Set Password/Cancel buttons
 *
 */
@Component({
  selector: `bfx-dialog-change-password`,
  templateUrl: `./html/bfx-dialog-change-password.html`,
  standalone: false,
})
export class BfxDialogChangePasswordComponent
  extends BfxDialogBaseComponent
  implements OnDestroy, OnInit, AfterViewInit
{
  public data = {
    dialogId: null,
    dialogEventSubject: new Subject<BfxDialogData>(),
    actionEvent: null,
    payload: null,
    dialogData: {
      show: false,
      width: 550,
      title_text: "TITLE",
      field: {
        old: "old",
        new: "new",
        confirm: "confirm",
      },
      action: {
        change: "change",
        cancel: "cancel",
      },
      general: {
        warning: "Warning etc",
      },
    },
  };

  public passwordProgress: number = 0;
  public savePasswordDisabled: boolean = true;
  public changePasswordSubmitted: boolean = false;
  public generalPasswordError: string = "";
  public minPasswordLength: number = MINIMUM_PASSWORD_LENGTH;
  public maxPasswordLength: number = MAXIMUM_PASSWORD_LENGTH;
  public oldPassword: string = "";
  public newPassword: string = "";
  public newPasswordConfirmation: string = "";
  public newPasswordStrength: number = 0;
  public newPasswordStrengthMessage: string = "No";
  public newPasswordStrengthColor: string = "";
  public newPasswordSuccess: boolean = false;
  public passwordErrors: any = {};
  public validPasswordField: any = {};
  public failedCheckFields: any = {};

  public passwordWarningMessage: string;
  public nativeDesktop: boolean;
  public activeUser: ActiveUser;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(
    public systemConfigService: SystemConfigPromiseService,
    public userService: UserPromiseLookupService,
    public activeUserService: ActiveUserProfilePromiseService,
    private _translate: TranslateService,
    protected _element: ElementRef,
  ) {
    super();
  }

  ngOnInit() {
    this.data.dialogData.title_text = this._translate.instant("changepassword.title");
    this.data.dialogData.field.old = this._translate.instant("changepassword.field.old");
    this.data.dialogData.field.new = this._translate.instant("changepassword.field.new");
    this.data.dialogData.field.confirm = this._translate.instant("changepassword.field.confirm");
    this.data.dialogData.action.change = this._translate.instant("changepassword.action.change");
    this.data.dialogData.action.cancel = this._translate.instant("changepassword.action.cancel");
    this.data.dialogData.general.warning = this._translate.instant(
      "changepassword.general.warning",
    );
  }

  ngOnDestroy() {
    //
  }

  ngAfterViewInit() {
    //Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    //Add 'implements AfterViewInit' to the class.
    this.activeUserService.getActiveUserData().then((data: ActiveUser) => {
      this.activeUser = data;
    });
  }

  public initialize(): void {
    this.systemConfigService
      .getSystemConfig(MINIMUM_PASSWORD_LENGTH_SYSCONF)
      .then((value: string) => {
        if (value != null) {
          try {
            this.minPasswordLength = parseInt(value);
          } catch (error) {
            logger.out.error(
              "Error occured while parsing system config: " + MINIMUM_PASSWORD_LENGTH_SYSCONF,
            );
          }
        }
      });

    this.systemConfigService
      .getSystemConfig(MAXIMUM_PASSWORD_LENGTH_SYSCONF)
      .then((value: string) => {
        if (value != null) {
          try {
            this.maxPasswordLength = parseInt(value);
          } catch (error) {
            logger.out.error(
              "Error occured while parsing system config: " + MAXIMUM_PASSWORD_LENGTH_SYSCONF,
            );
          }
        }
      });

    this.nativeDesktop = NativeDesktopSupport.isNativeDesktopContainer();
  }

  /**********************************************************************/
  /* OPEN DIALOG */
  public openDialog(_dialogData: BfxDialogData): Observable<any> {
    try {
      this.data.dialogId = _dialogData.dialogId;
      this.data.actionEvent = _dialogData.event;
      this.data.payload = _dialogData.payload;

      this.data.dialogData.show = true;
      NativeDesktopSupport.prepareEmbeddedDialog(_dialogData.body, this._dialog, this._element);
    } catch (ex) {
      logger.out.error(
        "ERROR: BfxDialogOrderCancelConfirmComponent.openDialog(): Dialog exception! msg:",
        ex,
      );
    }

    return this.data.dialogEventSubject.asObservable();
  }

  public closeDialog(): void {
    if (typeof this.data.payload?.callback === "function") {
      this.data.payload.callback(this.newPasswordSuccess);
    }

    super.closeDialog();

    this.data.actionEvent = null;
    this.data.payload = null;
    this.data.dialogData.show = false;

    // inform dialog service that dialog is closed and to cleanup
    this.notifyDialogClosed(this.data.dialogEventSubject, this.data.dialogId);
  }

  public changePassword(): void {
    this.validatePasswordField(PasswordField.OLD_PASSWORD);
    this.validatePasswordField(PasswordField.NEW_PASSWORD);
    this.validatePasswordField(PasswordField.NEW_PASSWORD_CONFIRM);

    if (this.savePasswordDisabled) {
      return;
    }

    this.savePasswordDisabled = true;
    this.changePasswordSubmitted = true;
    this.passwordWarningMessage = "";
    this.generalPasswordError = "";
    this.failedCheckFields = {};

    this.userService
      .changePassword(
        this.oldPassword,
        this.newPassword,
        this.newPasswordConfirmation,
        false,
        this.activeUser.userCode,
      )
      .then((response: any) => {
        let error: boolean = false;

        this.changePasswordSubmitted = this.savePasswordDisabled = false;

        if (response != null) {
          let errors: ErrorsImpl = new ErrorsImpl();

          if (errors.encodableType == response.encodableType) {
            error = true;

            let errorMessage: string = this._translate.instant("user.settings.generic.error");

            this.passwordErrors = {};
            Object.assign(errors, response);
            if (errors.errorMessages != null && errors.errorMessages.length > 0) {
              let errorObject: any = JSON.parse(errors.errorMessages[0] + "");
              errorMessage = this.convertError(errors.errorMessages);
              this.generalPasswordError = errorMessage;

              if (SYSTEM_ERRORS.indexOf(errorObject.id) >= 0) {
              } else if (OLD_PASS_ERRORS.indexOf(errorObject.id) >= 0) {
                this.passwordErrors[PasswordField.OLD_PASSWORD] = errorMessage + ".";
                this.failedCheckFields[PasswordField.OLD_PASSWORD] = this.oldPassword;
                this.validPasswordField[PasswordField.OLD_PASSWORD] = false;
                this.savePasswordDisabled = true;
              } else {
                this.passwordErrors[PasswordField.NEW_PASSWORD] = errorMessage;
                this.failedCheckFields[PasswordField.NEW_PASSWORD] = this.newPassword;
                this.validPasswordField[PasswordField.NEW_PASSWORD] = false;
                this.savePasswordDisabled = true;
              }
            }
          }
        }

        if (!error) {
          this.newPasswordSuccess = true;
          this.closeDialog();
        } else {
          this.newPasswordSuccess = false;
        }
      })
      .catch((_error: any) => {
        this.changePasswordSubmitted = this.savePasswordDisabled = false;
      });
  }

  public cancelChangePassword(): void {
    this.newPasswordSuccess = false;
    this.closeDialog();
  }

  public validatePasswordField(passwordField: PasswordField): void {
    let value: boolean = false;
    switch (passwordField) {
      case PasswordField.OLD_PASSWORD:
        value =
          this.oldPassword != null &&
          this.oldPassword.length >= this.minPasswordLength &&
          this.failedCheckFields[PasswordField.OLD_PASSWORD] != this.oldPassword;

        if (this.failedCheckFields[PasswordField.OLD_PASSWORD] != this.oldPassword) {
          delete this.failedCheckFields[PasswordField.OLD_PASSWORD];
        }

        if (this.oldPassword != null && this.oldPassword.length < this.minPasswordLength) {
          this.passwordErrors[PasswordField.OLD_PASSWORD] = this._translate.instant(
            "user.settings.oldpassword.too.short.error",
            [this.minPasswordLength],
          );
        } else if (value === true) {
          this.passwordErrors[PasswordField.OLD_PASSWORD] = "";
        }

        break;
      case PasswordField.NEW_PASSWORD:
        this.checkPasswordStrength();
        value =
          this.newPassword != null &&
          this.newPassword.length >= this.minPasswordLength &&
          this.failedCheckFields[PasswordField.NEW_PASSWORD] != this.newPassword;

        if (this.failedCheckFields[PasswordField.NEW_PASSWORD] != this.newPassword) {
          delete this.failedCheckFields[PasswordField.NEW_PASSWORD];
        }

        if (this.newPassword != null && this.newPassword.length < this.minPasswordLength) {
          this.passwordErrors[PasswordField.NEW_PASSWORD] = this._translate.instant(
            "user.settings.newpassword.too.short.error",
            [this.minPasswordLength],
          );
        }

        if (
          this.newPasswordConfirmation != null &&
          this.newPasswordConfirmation.length > 0 &&
          this.newPasswordConfirmation != this.newPassword
        ) {
          this.passwordErrors[PasswordField.NEW_PASSWORD_CONFIRM] = this._translate.instant(
            "user.settings.passwords.dont.match.error",
          );
          this.validPasswordField[PasswordField.NEW_PASSWORD_CONFIRM] = false;
        }

        if (value === true) {
          this.passwordErrors[PasswordField.NEW_PASSWORD] = "";
        }

        break;
      case PasswordField.NEW_PASSWORD_CONFIRM:
        value =
          this.newPasswordConfirmation != null &&
          this.newPasswordConfirmation.length > 0 &&
          this.newPasswordConfirmation.localeCompare(this.newPassword) == 0;
        this.passwordErrors[PasswordField.NEW_PASSWORD_CONFIRM] = "";

        if (
          this.newPasswordConfirmation != null &&
          this.newPasswordConfirmation.length > 0 &&
          !value
        ) {
          this.passwordErrors[PasswordField.NEW_PASSWORD_CONFIRM] = this._translate.instant(
            "user.settings.passwords.dont.match.error",
          );
        } else if (this.newPasswordConfirmation.length == 0 && this.newPassword.length == 0) {
          this.passwordErrors[PasswordField.NEW_PASSWORD_CONFIRM] = this._translate.instant(
            "user.settings.newpassword.too.short.error",
            [this.minPasswordLength],
          );
        }

        break;
    }

    this.validPasswordField[passwordField] = value;

    // All three fields need to populated and new password should match confirmation
    let keys: Array<string> = Object.keys(this.validPasswordField);
    this.savePasswordDisabled = !(
      keys.length == 3 &&
      keys
        .map((value: string) => this.validPasswordField[value])
        .every((value: boolean) => value != null && value === true)
    );

    if (!this.savePasswordDisabled) {
      this.passwordErrors = {};
      this.passwordWarningMessage = "";
    } else {
      this.passwordWarningMessage = this.createWarningMessage();
    }
    if (this.passwordWarningMessage) {
      // We don't need to have error displayed when user has changed the input
      this.generalPasswordError = "";
    }
  }

  private createWarningMessage() {
    if (this.passwordErrors[PasswordField.OLD_PASSWORD]) {
      return this.passwordErrors[PasswordField.OLD_PASSWORD];
    }
    if (this.passwordErrors[PasswordField.NEW_PASSWORD]) {
      return this.passwordErrors[PasswordField.NEW_PASSWORD];
    }
    return this.passwordErrors[PasswordField.NEW_PASSWORD_CONFIRM];
  }

  public checkPasswordStrength(): void {
    this.newPasswordStrength = this.calculateStrength(this.newPassword);
    if (this.newPasswordStrength > 0) {
      this.newPasswordStrengthMessage = STRENGTH_MESSAGES[this.newPasswordStrength - 1];
      this.newPasswordStrengthColor = STRENGTH_COLORS[this.newPasswordStrength - 1];
    } else {
      this.newPasswordStrengthMessage = "No";
      this.newPasswordStrengthColor = STRENGTH_COLORS[0];
    }
  }

  public calculateStrength(password: string): number {
    let strength: number = 0;

    let regSmall: RegExp = new RegExp(/([a-z]+)/);
    let regBig: RegExp = new RegExp(/([A-Z]+)/);
    let regNum: RegExp = new RegExp(/([0-9]+)/);
    let regSpecial: RegExp = new RegExp(/(\W+)/);

    if (password.search(regSmall) != -1) {
      strength++;
    }

    if (password.search(regBig) != -1) {
      strength++;
    }

    if (password.search(regNum) != -1) {
      strength++;
    }

    if (password.search(regSpecial) != -1) {
      strength++;
    }

    return strength;
  }
  /**
   * Creates a translation object with a message as id and
   * parameters as args list from a string
   * @param error the error string as served in ErrorsImpl object
   */
  protected convertError(error: any): string {
    let errorObject = JSON.parse(error + "");
    return this._translate.instant(errorObject.id, errorObject.args);
  }
}
